/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import {
  useListElasticsearchProjectsQuery,
  useListObservabilityProjectsQuery,
  useListSecurityProjectsQuery,
} from '@modules/project-admin-lib/hooks/list'
import type { AnyAdminProject } from '@modules/project-admin-api/types'
import { useConfig } from '@modules/cui/ConfigContext'
import Feature from '@modules/utils/feature'

import type React from 'react'

type ChildProps = {
  projectsLength: number
  /**
   * Check before using metadata. False indicates a loading or failed state.
   */
  isReady: boolean
}

type Props = {
  organizationId?: string
  children: (props: ChildProps) => React.ReactElement<ChildProps, any> | null
}

const SupplyProjectsMetadata: React.FunctionComponent<Props> = ({ organizationId, children }) => {
  const serverlessFeature = useConfig(Feature.serverless)

  const queryParameters = {
    organization_id: organizationId,
  }

  const elasticsearchQuery = useListElasticsearchProjectsQuery(queryParameters, {
    enabled: serverlessFeature,
  })
  const observaibilityQuery = useListObservabilityProjectsQuery(queryParameters, {
    enabled: serverlessFeature,
  })
  const securityQuery = useListSecurityProjectsQuery(queryParameters, {
    enabled: serverlessFeature,
  })

  const queries = [elasticsearchQuery, observaibilityQuery, securityQuery]
  const successfulQueries = queries.filter((query) => query.isSuccess)

  const isLoading = queries.some((query) => query.isLoading)

  const items: AnyAdminProject[] = []

  for (const query of successfulQueries) {
    const dataItems = query.data?.items

    if (dataItems) {
      items.push(...dataItems)
    }
  }

  return children({ projectsLength: items.length, isReady: !isLoading })
}

export default SupplyProjectsMetadata
